
import { useCallback, startTransition } from 'react';
import { Device } from '@twilio/voice-sdk';
import { UseTwilioCallLogsReturn } from '../useTwilioCallLogs';
import { useCallStateManager } from './call-actions/useCallStateManager';
import { useCallEventHandlers } from './call-actions/useCallEventHandlers';
import { useCallOperations } from './call-actions/useCallOperations';
import { useCallStatusPolling } from './useCallStatusPolling';
import { useAuth } from '@/context/AuthContext';

export const useTwilioCallActions = (
  deviceRef: React.MutableRefObject<Device | null>,
  callInProgress: boolean,
  setCallInProgress: (value: boolean) => void,
  setCallStatus: (status: string) => void,
  setCallStartTime: (time: Date) => void,
  setCallSid: (sid: string) => void,
  setIsMuted: (muted: boolean) => void,
  setActiveContactId: (contactId: number | null) => void,
  setActiveContactName: (contactName: string | null) => void,
  resetCallState: () => void,
  endCallState: () => void,
  _startStatusPolling: (callSid: string) => void, // Keep for compatibility but use our improved version
  _stopStatusPolling: () => void, // Keep for compatibility but use our improved version
  initializeDevice: () => Promise<Device | null>,
  callLogActions: UseTwilioCallLogsReturn,
  handleTokenExpiry?: () => Promise<Device | null>
) => {
  const { user } = useAuth();
  
  // Use improved status polling
  const { startStatusPolling, stopStatusPolling } = useCallStatusPolling();
  // State management
  const {
    updateCallToConnecting,
    updateCallSid,
    updateCallStatus,
    resetCall,
    endCall: endCallAction
  } = useCallStateManager(
    setCallInProgress,
    setCallStatus,
    setCallStartTime,
    setCallSid,
    setIsMuted,
    setActiveContactId,
    setActiveContactName,
    resetCallState,
    endCallState
  );

  // Clear existing calls utility
  const clearExistingCalls = useCallback(() => {
    if (deviceRef.current && deviceRef.current.state !== 'destroyed') {
      const calls = deviceRef.current.calls;
      if (calls && calls.length > 0) {
        console.log('🧹 Clearing existing calls before making new call');
        calls.forEach(call => {
          try {
            call.disconnect();
          } catch (error) {
            console.warn('Warning while disconnecting existing call:', error);
          }
        });
      }
    }
    console.log('🔄 Clearing existing calls - resetting state');
    startTransition(() => {
      resetCallState();
      stopStatusPolling();
      callLogActions.clearSession();
    });
  }, [resetCallState, stopStatusPolling, callLogActions]);

  // Call operations
  const {
    makeCall,
    endCall,
    toggleMute,
    sendDTMF,
    callLogIdRef,
    activeCallRef,
    handleCallSidAvailable
  } = useCallOperations(
    deviceRef,
    callInProgress,
    callLogActions,
    updateCallToConnecting,
    updateCallSid,
    resetCall,
    clearExistingCalls,
    initializeDevice,
    startStatusPolling,
    stopStatusPolling,
    setIsMuted,
    (call) => setupCallEventListeners(call),
    handleTokenExpiry,
    user?.id
  );

  // Event handlers
  const {
    handleCallAccept,
    handleCallRinging,
    handleCallDisconnect,
    handleCallCancel,
    handleCallError,
    handleCallStatus
  } = useCallEventHandlers(
    callLogIdRef,
    activeCallRef,
    callLogActions,
    updateCallStatus,
    endCallAction,
    stopStatusPolling,
    handleCallSidAvailable
  );

  // Setup event listeners for a call
  const setupCallEventListeners = useCallback((call: any) => {
    call.on('accept', () => handleCallAccept(call));
    call.on('ringing', () => handleCallRinging(call));
    call.on('disconnect', () => handleCallDisconnect(call));
    call.on('cancel', handleCallCancel);
    call.on('error', handleCallError);
    call.on('status', (status: string) => handleCallStatus(call, status));
  }, [
    handleCallAccept,
    handleCallRinging,
    handleCallDisconnect,
    handleCallCancel,
    handleCallError,
    handleCallStatus
  ]);

  return {
    makeCall,
    endCall,
    toggleMute,
    sendDTMF
  };
};
